import { useQuery } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	ContentType,
	MetaAdsAdFormat,
	MetaAdsCampaignObjective,
	MetaAdsConversionLocation,
	type MetaAdsCTA,
	MetaAdsOptimizationGoal,
} from "shared";
import { Api } from "../api.config";
import { useModal } from "../common/modal/Modal";
import { useCompany } from "../company/useCompany";
import { Card } from "../components/Card";
import { LinkGen, QueryKeys } from "../LinkGen";
import { useCampaignWizard } from "./CampaignWizardContext";
import { CreativeMediaPreview } from "./CreativeMediaPreview";
import { ModalSubmitForApproval } from "./ModalSubmitForApproval";
import {
	metaAdsPreviewFiles,
	metaAdsUseCarouselPreview,
} from "./metaAdsCreative";
import { formatMetaAdsMoney } from "./metaAdsMoney";
import { ModalSaveAsTemplate } from "./templates/ModalSaveAsTemplate";
import { useApprovalActions } from "./useApprovals";
import { useCampaignDraftActions } from "./useCampaignDrafts";
import { useCampaignTemplateActions } from "./useCampaignTemplates";

const CTA_LABELS: Record<MetaAdsCTA, string> = {
	LEARN_MORE: "Learn More",
	SHOP_NOW: "Shop Now",
	SIGN_UP: "Sign Up",
	CONTACT_US: "Contact Us",
	GET_QUOTE: "Get Quote",
	BOOK_NOW: "Book Now",
	SEND_MESSAGE: "Send Message",
	LIKE_PAGE: "Like Page",
	CALL_NOW: "Call Now",
	BUY_NOW: "Buy Now",
	ORDER_NOW: "Order Now",
};

const AdPreview: FC<{
	companyName: string;
	primaryText: string;
	headline: string;
	description: string;
	callToAction: MetaAdsCTA;
	destinationUrl: string;
	displayLink: string;
	mediaFileIds: number[];
	adFormat: MetaAdsAdFormat;
	variant: "feed" | "stories";
}> = ({
	companyName,
	primaryText,
	headline,
	description,
	callToAction,
	destinationUrl,
	displayLink,
	mediaFileIds,
	adFormat,
	variant,
}) => {
	const { data: mediaFiles } = useQuery({
		queryKey: ["MEDIA_BATCH", ...mediaFileIds],
		queryFn: () => Api.GetMediaBatch(mediaFileIds).then((r) => r.data),
		enabled: mediaFileIds.length > 0,
	});

	const hasMedia = (mediaFiles?.length ?? 0) > 0;
	const previewFiles = metaAdsPreviewFiles(mediaFiles, adFormat, mediaFileIds);
	const useCarouselLayout = metaAdsUseCarouselPreview(
		adFormat,
		mediaFileIds,
		previewFiles,
	);
	const domain = (() => {
		try {
			return displayLink || new URL(destinationUrl).hostname;
		} catch {
			return displayLink;
		}
	})();

	if (variant === "stories") {
		return (
			<div
				style={{
					background: "#000",
					borderRadius: 16,
					maxWidth: 280,
					margin: "0 auto",
					aspectRatio: "9/16",
					position: "relative",
					overflow: "hidden",
					fontFamily: "Helvetica, Arial, sans-serif",
				}}
			>
				{hasMedia && previewFiles.length > 0 ? (
					<div
						style={{
							position: "absolute",
							inset: 0,
						}}
					>
						<CreativeMediaPreview
							files={previewFiles}
							variant="full"
							fill
							showControls={false}
							layout={useCarouselLayout ? "carousel" : "grid"}
							aspectRatio="9 / 16"
						/>
					</div>
				) : (
					<div
						style={{
							width: "100%",
							height: "100%",
							background: "#1c1e21",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "#fff",
							fontSize: 14,
						}}
					>
						{mediaFileIds.length > 0 ? "Loading..." : "No media selected"}
					</div>
				)}
				<div
					style={{
						position: "absolute",
						top: 12,
						left: 12,
						right: 12,
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}
				>
					<div
						style={{
							width: 32,
							height: 32,
							borderRadius: "50%",
							background: "#4267B2",
							color: "#fff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontWeight: 700,
							fontSize: 14,
						}}
					>
						{companyName.charAt(0).toUpperCase()}
					</div>
					<div style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
						{companyName}
					</div>
				</div>
				<div
					style={{
						position: "absolute",
						bottom: 0,
						left: 0,
						right: 0,
						padding: 16,
						background: "linear-gradient(transparent, rgba(0,0,0,0.85))",
						color: "#fff",
					}}
				>
					{primaryText && (
						<div style={{ fontSize: 14, marginBottom: 8 }}>{primaryText}</div>
					)}
					<div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
						{headline || "Headline"}
					</div>
					{domain && (
						<div style={{ fontSize: 12, opacity: 0.8, marginBottom: 12 }}>
							{domain}
						</div>
					)}
					<div
						style={{
							background: "#fff",
							color: "#000",
							textAlign: "center",
							padding: "10px 16px",
							borderRadius: 24,
							fontWeight: 600,
							fontSize: 14,
						}}
					>
						{CTA_LABELS[callToAction] ?? "Learn More"}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			style={{
				background: "#fff",
				border: "1px solid #dddfe2",
				borderRadius: 8,
				maxWidth: 500,
				margin: "0 auto",
				fontFamily: "Helvetica, Arial, sans-serif",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					padding: 12,
				}}
			>
				<div
					style={{
						width: 40,
						height: 40,
						borderRadius: "50%",
						background: "#4267B2",
						color: "#fff",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontWeight: 700,
						fontSize: 16,
						flexShrink: 0,
					}}
				>
					{companyName.charAt(0).toUpperCase()}
				</div>
				<div>
					<div style={{ fontWeight: 600, fontSize: 14, color: "#1c1e21" }}>
						{companyName}
					</div>
					<div style={{ fontSize: 12, color: "#65676b" }}>Sponsored</div>
				</div>
			</div>

			{primaryText && (
				<div
					style={{
						padding: "0 12px 12px",
						fontSize: 15,
						color: "#1c1e21",
						whiteSpace: "pre-wrap",
					}}
				>
					{primaryText}
				</div>
			)}

			{hasMedia && previewFiles.length > 0 ? (
				<CreativeMediaPreview
					files={previewFiles}
					variant="full"
					layout={useCarouselLayout ? "carousel" : "grid"}
					aspectRatio="1 / 1"
				/>
			) : (
				<div
					style={{
						width: "100%",
						aspectRatio: "16/9",
						background: "#f0f2f5",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: "#65676b",
						fontSize: 14,
					}}
				>
					{mediaFileIds.length > 0 ? "Loading media..." : "No media selected"}
				</div>
			)}

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					padding: "10px 12px",
					background: "#f0f2f5",
					borderBottom: "1px solid #dddfe2",
				}}
			>
				<div style={{ flex: 1, minWidth: 0 }}>
					{domain && (
						<div
							style={{
								fontSize: 12,
								color: "#65676b",
								textTransform: "uppercase",
							}}
						>
							{domain}
						</div>
					)}
					<div
						style={{
							fontSize: 15,
							fontWeight: 600,
							color: "#1c1e21",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{headline || "Headline"}
					</div>
					{description && (
						<div
							style={{
								fontSize: 13,
								color: "#65676b",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{description}
						</div>
					)}
				</div>
				<button
					type="button"
					style={{
						marginLeft: 12,
						padding: "6px 16px",
						background: "#e4e6eb",
						border: "none",
						borderRadius: 6,
						fontSize: 14,
						fontWeight: 600,
						color: "#1c1e21",
						cursor: "default",
						whiteSpace: "nowrap",
					}}
				>
					{CTA_LABELS[callToAction] ?? "Learn More"}
				</button>
			</div>
		</div>
	);
};

export const StepReview: FC = () => {
	const company = useCompany();
	const { draft, prevStep } = useCampaignWizard();
	const { saveDraft, publishDraft } = useCampaignDraftActions();
	const { saveAsTemplate } = useCampaignTemplateActions();
	const { submitForApproval } = useApprovalActions();
	const { open } = useModal();
	const navigate = useNavigate();
	const [publishing, setPublishing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [previewVariant, setPreviewVariant] = useState<"feed" | "stories">(
		"feed",
	);
	const [showApprovalModal, setShowApprovalModal] = useState(false);
	const [submittingApproval, setSubmittingApproval] = useState(false);

	const { data: approvalSettings } = useQuery({
		queryKey: QueryKeys.Integrations.MetaAds.ApprovalSettings(company.id),
		queryFn: () => Api.GetMetaAdsApprovalSettings().then((r) => r.data),
	});
	const approvalEnabled = approvalSettings?.enabled ?? false;

	const { data: reviewMediaFiles } = useQuery({
		queryKey: ["MEDIA_BATCH", ...draft.mediaFileIds],
		queryFn: () => Api.GetMediaBatch(draft.mediaFileIds).then((r) => r.data),
		enabled: draft.mediaFileIds.length > 0,
	});

	const carouselReviewVideoCount = useMemo(
		() =>
			reviewMediaFiles?.filter((f) => f.type === ContentType.VIDEO).length ?? 0,
		[reviewMediaFiles],
	);

	const isAwareness =
		draft.objective === MetaAdsCampaignObjective.OUTCOME_AWARENESS;
	const isEngagement =
		draft.objective === MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT;
	const isLeads = draft.objective === MetaAdsCampaignObjective.OUTCOME_LEADS;
	const isSales = draft.objective === MetaAdsCampaignObjective.OUTCOME_SALES;
	const isLeadsInstantForm =
		isLeads &&
		draft.conversionLocation === MetaAdsConversionLocation.INSTANT_FORM;

	const objectiveLabel: Record<MetaAdsCampaignObjective, string> = {
		[MetaAdsCampaignObjective.OUTCOME_AWARENESS]: "Awareness",
		[MetaAdsCampaignObjective.OUTCOME_TRAFFIC]: "Traffic",
		[MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT]: "Engagement",
		[MetaAdsCampaignObjective.OUTCOME_LEADS]: "Leads",
		[MetaAdsCampaignObjective.OUTCOME_SALES]: "Sales",
	};

	const noUrlNeeded =
		isLeadsInstantForm ||
		(isEngagement &&
			(draft.conversionLocation === MetaAdsConversionLocation.ON_POST ||
				draft.conversionLocation === MetaAdsConversionLocation.ON_PAGE)) ||
		(isSales && draft.conversionLocation === MetaAdsConversionLocation.CALLS);

	const errors: string[] = [];
	if (!draft.name.trim()) errors.push("Campaign name is required.");
	if (!noUrlNeeded && !draft.destinationUrl.trim())
		errors.push("Destination URL is required.");
	if (!draft.primaryText.trim()) errors.push("Primary text is required.");
	if (draft.budgetAmount <= 0) errors.push("Budget must be greater than 0.");
	if (draft.adFormat === MetaAdsAdFormat.CAROUSEL) {
		const carouselImages =
			reviewMediaFiles?.filter((f) => f.type === ContentType.IMAGE) ?? [];
		if (carouselImages.length < 2) {
			errors.push("Carousel ads require at least 2 images.");
		}
		if (carouselImages.length > 10) {
			errors.push("Carousel ads support at most 10 images.");
		}
		if (reviewMediaFiles?.some((f) => f.type === ContentType.VIDEO)) {
			errors.push(
				"Carousel ads use images only — go back to Creative and remove videos from your selection.",
			);
		}
	}
	const geo = draft.geoTargeting;
	const hasPins = (geo?.locationPins?.length ?? 0) > 0;
	const hasCountries =
		(geo?.countries?.length ?? 0) > 0 || draft.targetCountries.length > 0;
	if (!hasPins && !hasCountries)
		errors.push("At least one location or target country is required.");
	if (isLeads && isLeadsInstantForm && !draft.leadForm && !draft.leadFormMetaId)
		errors.push("Lead form is required for Leads + Instant Form.");
	if (
		isSales &&
		draft.conversionLocation === MetaAdsConversionLocation.WEBSITE &&
		!draft.pixelId.trim()
	)
		errors.push("Meta Pixel ID is required for Sales + Website.");
	if (
		isSales &&
		draft.conversionLocation === MetaAdsConversionLocation.CALLS &&
		!draft.phoneNumber?.trim()
	)
		errors.push("Phone number is required for Sales + Calls.");

	const handleSaveDraft = async () => {
		setSaving(true);
		try {
			await saveDraft.mutateAsync(draft);
			navigate(LinkGen.MetaAds.Index());
		} catch {
			// onError callback in useMutation handles the alert
		} finally {
			setSaving(false);
		}
	};

	const handlePublish = async () => {
		if (errors.length > 0) return;
		setPublishing(true);
		try {
			await saveDraft.mutateAsync(draft);
			await publishDraft.mutateAsync(draft.id);
			navigate(LinkGen.MetaAds.Index());
		} catch {
			// onError callback in useMutation handles the alert
		} finally {
			setPublishing(false);
		}
	};

	const handleSubmitForApproval = async (note?: string) => {
		if (errors.length > 0) return;
		setSubmittingApproval(true);
		try {
			await saveDraft.mutateAsync(draft);
			await submitForApproval.mutateAsync({
				draftId: draft.id,
				note,
			});
			navigate(LinkGen.MetaAds.Approvals());
		} catch {
			// onError callback in useMutation handles the alert
		} finally {
			setSubmittingApproval(false);
			setShowApprovalModal(false);
		}
	};

	const handleSaveAsTemplate = () => {
		open(ModalSaveAsTemplate, {
			onSave: async ({ name, description }) => {
				await saveDraft.mutateAsync(draft);
				await saveAsTemplate.mutateAsync({
					draftId: draft.id,
					dto: { name, description },
				});
			},
		});
	};

	const convLocationLabel = (() => {
		switch (draft.conversionLocation) {
			case MetaAdsConversionLocation.INSTANT_FORM:
				return "Instant Forms";
			case MetaAdsConversionLocation.WEBSITE:
				return "Website";
			case MetaAdsConversionLocation.MESSENGER:
				return "Messenger";
			case MetaAdsConversionLocation.ON_POST:
				return "On Your Ad (Post Engagement)";
			case MetaAdsConversionLocation.ON_PAGE:
				return "On Your Page (Page Likes)";
			case MetaAdsConversionLocation.CALLS:
				return "Phone Calls";
			case MetaAdsConversionLocation.INSTAGRAM_DIRECT:
				return "Instagram Direct";
			default:
				return "-";
		}
	})();

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
			<Card>
				<Card.Header>Campaign</Card.Header>
				<Card.Body>
					<dl style={{ marginBottom: 0 }}>
						<dt>Name</dt>
						<dd>{draft.name || "-"}</dd>
						<dt>Objective</dt>
						<dd>{objectiveLabel[draft.objective] ?? draft.objective}</dd>
						<dt>Campaign Budget Optimization</dt>
						<dd>{draft.campaignBudgetOptimization ? "Yes" : "No"}</dd>
						{draft.campaignBudgetOptimization && (
							<>
								<dt>Budget</dt>
								<dd>
									{draft.budgetType} - {formatMetaAdsMoney(draft.budgetAmount)}
								</dd>
							</>
						)}
					</dl>
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Ad Set</Card.Header>
				<Card.Body>
					<dl style={{ marginBottom: 0 }}>
						{!draft.campaignBudgetOptimization && (
							<>
								<dt>Budget</dt>
								<dd>
									{draft.budgetType} - {formatMetaAdsMoney(draft.budgetAmount)}
								</dd>
							</>
						)}
						{(isLeads || isEngagement || isSales) && (
							<>
								<dt>Conversion Location</dt>
								<dd>{convLocationLabel}</dd>
							</>
						)}
						{(isLeads || isSales) &&
							draft.conversionLocation ===
								MetaAdsConversionLocation.WEBSITE && (
								<>
									<dt>Meta Pixel ID</dt>
									<dd>{draft.pixelId || "-"}</dd>
								</>
							)}
						{isSales &&
							draft.conversionLocation ===
								MetaAdsConversionLocation.WEBSITE && (
								<>
									<dt>Conversion Event</dt>
									<dd>{draft.conversionEvent ?? "-"}</dd>
								</>
							)}
						{isSales &&
							draft.conversionLocation === MetaAdsConversionLocation.CALLS && (
								<>
									<dt>Phone Number</dt>
									<dd>{draft.phoneNumber || "-"}</dd>
								</>
							)}
						{isAwareness &&
							draft.optimizationGoal === MetaAdsOptimizationGoal.REACH && (
								<>
									<dt>Frequency Cap</dt>
									<dd>
										{draft.frequencyCapImpressions ?? 2} impressions per{" "}
										{draft.frequencyCapIntervalDays ?? 7} days
									</dd>
								</>
							)}
						{isSales && (
							<>
								<dt>Dynamic Creative Optimization</dt>
								<dd>{draft.dynamicCreativeOptimization ? "Yes" : "No"}</dd>
								<dt>Attribution Windows</dt>
								<dd>
									{(draft.attributionWindows ?? []).join(", ") || "Default"}
								</dd>
							</>
						)}
						{isEngagement && draft.messagingGreeting && (
							<>
								<dt>Messaging Greeting</dt>
								<dd>{draft.messagingGreeting}</dd>
							</>
						)}
						<dt>Schedule</dt>
						<dd>
							{draft.startDate}
							{draft.endDate ? ` to ${draft.endDate}` : " (no end date)"}
						</dd>
						<dt>Placements</dt>
						<dd>
							{draft.placements === "automatic"
								? "Automatic"
								: Array.isArray(draft.placements)
									? draft.placements.join(", ")
									: "-"}
						</dd>
						<dt>Geo-Targeting</dt>
						<dd>
							{hasPins
								? `${geo?.locationPins.length} location(s): ${geo?.locationPins.map((p) => p.label).join("; ")}`
								: `Countries: ${(geo?.countries?.length ? geo.countries : draft.targetCountries).join(", ")}`}
						</dd>
						{hasPins && (
							<>
								<dt>Location reach</dt>
								<dd>People living in or recently in selected locations</dd>
							</>
						)}
						<dt>Age Range</dt>
						<dd>
							{draft.ageMin} - {draft.ageMax}
						</dd>
						<dt>Gender</dt>
						<dd>
							{draft.genders.includes(0)
								? "All"
								: draft.genders.includes(1)
									? "Male"
									: "Female"}
						</dd>
						<dt>Optimization</dt>
						<dd>{draft.optimizationGoal}</dd>
						<dt>Bid Strategy</dt>
						<dd>
							{draft.bidStrategy}
							{draft.bidAmount
								? ` (cap: ${formatMetaAdsMoney(draft.bidAmount)})`
								: ""}
						</dd>
						{((draft.includedAudiences?.length ?? 0) > 0 ||
							(draft.excludedAudiences?.length ?? 0) > 0) && (
							<>
								<dt>Audiences</dt>
								<dd>
									{(draft.includedAudiences?.length ?? 0) > 0 &&
										`Include: ${draft.includedAudiences?.length} audience(s)`}
									{(draft.includedAudiences?.length ?? 0) > 0 &&
										(draft.excludedAudiences?.length ?? 0) > 0 &&
										" | "}
									{(draft.excludedAudiences?.length ?? 0) > 0 &&
										`Exclude: ${draft.excludedAudiences?.length} audience(s)`}
								</dd>
							</>
						)}
					</dl>
				</Card.Body>
			</Card>

			{isLeads && (draft.leadForm || draft.leadFormMetaId) && (
				<Card>
					<Card.Header>Lead Form</Card.Header>
					<Card.Body>
						<dl style={{ marginBottom: 0 }}>
							{draft.leadFormMetaId && (
								<>
									<dt>Source</dt>
									<dd>Existing Meta Instant Form</dd>
								</>
							)}
							<dt>Form Name</dt>
							<dd>{draft.leadForm?.name || "-"}</dd>
							{draft.leadFormMetaId ? (
								<>
									<dt>Meta Form ID</dt>
									<dd>{draft.leadFormMetaId}</dd>
								</>
							) : (
								<>
									<dt>Questions ({draft.leadForm?.questions.length ?? 0})</dt>
									<dd>
										{draft.leadForm?.questions.map((q) => q.label).join(", ") ||
											"-"}
									</dd>
									<dt>Privacy Policy</dt>
									<dd>{draft.leadForm?.privacyPolicyUrl || "-"}</dd>
									<dt>Thank You Screen</dt>
									<dd>
										{draft.leadForm?.thankYouTitle}
										{draft.leadForm?.thankYouDescription
											? ` - ${draft.leadForm.thankYouDescription}`
											: ""}
									</dd>
								</>
							)}
						</dl>
					</Card.Body>
				</Card>
			)}

			<Card>
				<Card.Header>Ad Preview</Card.Header>
				<Card.Body>
					{(draft.adFormat === MetaAdsAdFormat.CAROUSEL ||
						draft.mediaFileIds.length > 1) && (
						// biome-ignore lint/a11y/useSemanticElements: Bootstrap alert is not form output
						<div className="alert alert-info py-2 mb-3" role="status">
							Carousel ads use <strong>images only</strong> (2–10 images).
							Videos cannot be included. Swipe the preview to see each card.
						</div>
					)}
					{(draft.adFormat === MetaAdsAdFormat.CAROUSEL ||
						draft.mediaFileIds.length > 1) &&
						carouselReviewVideoCount > 0 && (
							<div className="alert alert-warning py-2 mb-3" role="alert">
								<strong>{carouselReviewVideoCount} video(s)</strong> are still
								in your selection and must be removed before publishing. Go back
								to Creative and tap <strong>Remove videos</strong>.
							</div>
						)}
					<div
						style={{
							display: "flex",
							gap: 8,
							marginBottom: 16,
							justifyContent: "center",
						}}
					>
						<button
							type="button"
							className={`btn btn-sm ${previewVariant === "feed" ? "btn-primary" : "btn-outline-secondary"}`}
							onClick={() => setPreviewVariant("feed")}
						>
							Feed
						</button>
						<button
							type="button"
							className={`btn btn-sm ${previewVariant === "stories" ? "btn-primary" : "btn-outline-secondary"}`}
							onClick={() => setPreviewVariant("stories")}
						>
							Stories
						</button>
					</div>
					<AdPreview
						companyName={company.name}
						primaryText={draft.primaryText}
						headline={draft.headline}
						description={draft.description}
						callToAction={draft.callToAction}
						destinationUrl={draft.destinationUrl}
						displayLink={draft.displayLink}
						mediaFileIds={draft.mediaFileIds}
						adFormat={draft.adFormat}
						variant={previewVariant}
					/>
				</Card.Body>
			</Card>

			{errors.length > 0 && (
				<Card>
					<Card.Header>Validation</Card.Header>
					<Card.Body>
						<ul className="text-danger" style={{ marginBottom: 0 }}>
							{errors.map((err) => (
								<li key={err}>{err}</li>
							))}
						</ul>
					</Card.Body>
				</Card>
			)}

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<button
					type="button"
					className="btn btn-outline-secondary"
					onClick={prevStep}
					disabled={publishing || saving || submittingApproval}
				>
					Back
				</button>
				<div style={{ display: "flex", gap: 12 }}>
					<button
						type="button"
						className="btn btn-outline-primary"
						onClick={handleSaveDraft}
						disabled={publishing || saving || submittingApproval}
					>
						{saving ? "Saving..." : "Save as Draft"}
					</button>
					<button
						type="button"
						className="btn btn-outline-secondary"
						onClick={handleSaveAsTemplate}
						disabled={publishing || saving || submittingApproval}
					>
						Save as Template
					</button>
					{approvalEnabled ? (
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => setShowApprovalModal(true)}
							disabled={
								errors.length > 0 || publishing || saving || submittingApproval
							}
						>
							Submit for Approval
						</button>
					) : (
						<button
							type="button"
							className="btn btn-primary"
							onClick={handlePublish}
							disabled={errors.length > 0 || publishing || saving}
						>
							{publishing
								? draft.metaCampaignId
									? "Saving..."
									: "Publishing..."
								: draft.metaCampaignId
									? "Save Changes"
									: "Publish"}
						</button>
					)}
				</div>
			</div>

			{showApprovalModal && (
				<ModalSubmitForApproval
					onSubmit={handleSubmitForApproval}
					onClose={() => setShowApprovalModal(false)}
					submitting={submittingApproval}
				/>
			)}
		</div>
	);
};
